import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogOut, Trophy, TrendingUp, Users, Award, Eye, EyeOff, ChevronDown, ChevronUp, ArrowUp, ArrowDown, Lock, Unlock, Trash2, MinusCircle, Download, Minus, Plus } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import ThemeToggle from '@/components/ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ user, onLogout }) {
  const [boysResults, setBoysResults] = useState([]);
  const [girlsResults, setGirlsResults] = useState([]);
  const [allResults, setAllResults] = useState([]);
  const [categoryResults, setCategoryResults] = useState([]);
  const [allScores, setAllScores] = useState([]);
  const [subcategoryMap, setSubcategoryMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showScores, setShowScores] = useState(true);
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  const { requestMove, dialog: tieBreakDialog } = useTieBreakMove(() => fetchData());

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [boysRes, girlsRes, allRes, categoryRes, scoresRes, subcatsRes] = await Promise.all([
        axios.get(`${API}/results/overall?gender=Boys`, { headers }),
        axios.get(`${API}/results/overall?gender=Girls`, { headers }),
        axios.get(`${API}/results/overall`, { headers }),
        axios.get(`${API}/results/categories`, { headers }),
        axios.get(`${API}/scores/all`, { headers }),
        axios.get(`${API}/subcategories`, { headers }),
      ]);

      setBoysResults(boysRes.data);
      setGirlsResults(girlsRes.data);
      setAllResults(allRes.data);
      setCategoryResults(categoryRes.data);
      setAllScores(scoresRes.data);
      const map = {};
      subcatsRes.data.forEach((s) => {
        map[s.id] = { name: s.name, max_score: s.max_score };
      });
      setSubcategoryMap(map);
    } catch (error) {
      toast.error('Gagal memuatkan keputusan');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-zinc-400">Memuatkan keputusan...</div>
      </div>
    );
  }

  const totalTeams = allResults.length;
  const totalScores = allScores.length;

  // Custom category order requested by admin: Seni Kata → Juara → Tukang Karut →
  // Awak-Awak → Paluan → Lagu Juara Terbaik Pilihan Juri → Lagu Karut Pilihan Juri.
  const CATEGORY_ORDER = [
    'Seni Kata',
    'Juara',
    'Tukang Karut',
    'Awak-Awak',
    'Paluan',
    'Lagu Juara Terbaik Pilihan Juri',
    'Lagu Karut Pilihan Juri',
  ];
  const orderedCategories = [...categoryResults]
    .sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a.category_name);
      const bi = CATEGORY_ORDER.indexOf(b.category_name);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

  return (
    <div className="min-h-screen bg-[#09090b]">
      <header className="border-b border-zinc-800 bg-[#18181b] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white">Piala Suara Serumpun 2026</h1>
            <p className="text-xs text-zinc-500">Papan Pemuka Admin</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm text-white">{user.name}</div>
              <div className="text-xs text-zinc-500">Pentadbir</div>
            </div>
            <Button
              onClick={() => setShowScores((v) => !v)}
              data-testid="toggle-show-scores-button"
              variant="ghost"
              className="text-zinc-400 hover:text-white text-xs"
              title={showScores ? 'Sembunyikan markah' : 'Tunjuk markah'}
            >
              {showScores ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
              {showScores ? 'Sembunyikan Markah' : 'Tunjuk Markah'}
            </Button>
            <ThemeToggle />
            <Button
              onClick={onLogout}
              data-testid="admin-logout-button"
              variant="ghost"
              className="text-zinc-400 hover:text-white"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[#18181b] border border-zinc-800 p-6 rounded-sm">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 bg-[#DFFF00]/10 rounded-sm flex items-center justify-center">
                <Users className="h-6 w-6 text-[#DFFF00]" />
              </div>
              <div>
                <div className="text-2xl font-mono font-bold text-white">{totalTeams}</div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">Jumlah Pasukan</div>
              </div>
            </div>
          </div>

          <div className="bg-[#18181b] border border-zinc-800 p-6 rounded-sm">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 bg-green-500/10 rounded-sm flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <div className="text-2xl font-mono font-bold text-white">{totalScores}</div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">
                  Markah Dihantar
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#18181b] border border-zinc-800 p-6 rounded-sm">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 bg-blue-500/10 rounded-sm flex items-center justify-center">
                <Trophy className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <div className="text-2xl font-mono font-bold text-white">
                  {allResults.length > 0 ? (showScores ? allResults[0].team_name : '••••') : '-'}
                </div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider">Peneraju Keseluruhan</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="boys" className="w-full">
          <TabsList className="bg-[#18181b] border border-zinc-800">
            <TabsTrigger value="boys" data-testid="boys-tab">
              Kedudukan Lelaki
            </TabsTrigger>
            <TabsTrigger value="girls" data-testid="girls-tab">
              Kedudukan Perempuan
            </TabsTrigger>
            <TabsTrigger value="categories" data-testid="categories-tab">
              Pemenang Kategori
            </TabsTrigger>
            <TabsTrigger value="overall" data-testid="overall-tab">
              Semua Pasukan
            </TabsTrigger>
            <TabsTrigger value="judge-scores" data-testid="judge-scores-tab">
              <Eye className="h-4 w-4 mr-2" />
              Markah Pengadil
            </TabsTrigger>
            <TabsTrigger value="admin-controls" data-testid="admin-controls-tab">
              <Lock className="h-4 w-4 mr-2" />
              Kawalan Admin
            </TabsTrigger>
          </TabsList>

          {/* Boys Rankings */}
          <TabsContent value="boys" className="space-y-6 mt-6">
            <RankingsTable results={boysResults} title="Kedudukan Pasukan Lelaki" scope="overall:Boys" requestMove={requestMove} showScores={showScores} />
          </TabsContent>

          {/* Girls Rankings */}
          <TabsContent value="girls" className="space-y-6 mt-6">
            <RankingsTable results={girlsResults} title="Kedudukan Pasukan Perempuan" scope="overall:Girls" requestMove={requestMove} showScores={showScores} />
          </TabsContent>

          {/* Category Winners - Split by Gender. Hide plain "Lagu". */}
          <TabsContent value="categories" className="space-y-6 mt-6">
            {orderedCategories.map((category) => (
              <CategoryPodium key={category.category_id} category={category} requestMove={requestMove} showScores={showScores} />
            ))}
          </TabsContent>

          {/* All Teams */}
          <TabsContent value="overall" className="space-y-6 mt-6">
            <RankingsTable results={allResults} title="Kedudukan Keseluruhan (Semua Pasukan)" showScores={showScores} />
          </TabsContent>

          {/* Judge Scores Detail */}
          <TabsContent value="judge-scores" className="space-y-6 mt-6">
            <JudgeScoresView allScores={allScores} subcategoryMap={subcategoryMap} showScores={showScores} />
          </TabsContent>

          {/* Admin Controls */}
          <TabsContent value="admin-controls" className="space-y-6 mt-6">
            <AdminControlsPanel onRefresh={fetchData} allScores={allScores} />
          </TabsContent>
        </Tabs>
      </div>
      {tieBreakDialog}
    </div>
  );
}

async function callTieBreakMove(scope, team_id, direction, admin_name, reason) {
  const token = localStorage.getItem('token');
  await axios.post(
    `${API}/admin/tie-break/move`,
    { scope, team_id, direction, admin_name, reason },
    { headers: { Authorization: `Bearer ${token}` } }
  );
}

function TieBreakDialog({ open, team, direction, onCancel, onConfirm }) {
  const [adminName, setAdminName] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setAdminName('');
      setReason('');
      setBusy(false);
    }
  }, [open]);

  const confirm = async () => {
    if (!adminName.trim() || !reason.trim()) {
      toast.error('Nama dan sebab kedua-duanya diperlukan');
      return;
    }
    setBusy(true);
    try {
      await onConfirm(adminName.trim(), reason.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="bg-[#18181b] border border-zinc-800 text-white">
        <DialogHeader>
          <DialogTitle className="font-heading">Tukar Kedudukan</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Alih <span className="text-white font-semibold">{team?.team_name || 'pasukan'}</span>{' '}
            <span className="text-[#DFFF00]">{direction === 'up' ? 'naik' : 'turun'}</span> dalam pasukan yang seri. Tindakan ini akan direkodkan.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-zinc-300 font-semibold block mb-1">Nama anda (admin)</label>
            <input
              type="text"
              value={adminName}
              autoFocus
              onChange={(e) => setAdminName(e.target.value)}
              data-testid="tiebreak-admin-name-input"
              className="w-full bg-zinc-900 border border-zinc-700 focus:border-[#DFFF00] focus:outline-none text-white rounded-sm px-3 py-2"
              placeholder="cth. Nazim"
            />
          </div>
          <div>
            <label className="text-sm text-zinc-300 font-semibold block mb-1">Sebab pertukaran kedudukan</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="tiebreak-reason-input"
              className="w-full bg-zinc-900 border border-zinc-700 focus:border-[#DFFF00] focus:outline-none text-white rounded-sm px-3 py-2 min-h-[90px] resize-none"
              placeholder="Jelaskan kenapa pasukan ini perlu disusun semula dalam seri…"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={onCancel}
            variant="ghost"
            className="text-zinc-300 hover:text-white"
            disabled={busy}
          >
            Batal
          </Button>
          <Button
            onClick={confirm}
            disabled={busy}
            data-testid="tiebreak-confirm-button"
            className="bg-[#DFFF00] hover:bg-[#DFFF00]/90 text-black font-semibold"
          >
            {busy ? 'Menyimpan…' : 'Sahkan pertukaran'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useTieBreakMove(onRefresh) {
  const [pending, setPending] = useState(null); // { scope, team, direction }

  const request = (scope, team, direction) => {
    setPending({ scope, team, direction });
  };

  const cancel = () => setPending(null);

  const confirm = async (adminName, reason) => {
    if (!pending) return;
    try {
      await callTieBreakMove(pending.scope, pending.team.team_id, pending.direction, adminName, reason);
      toast.success(`${pending.team.team_name} dialihkan ${pending.direction === 'up' ? 'naik' : 'turun'}`);
      setPending(null);
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyusun semula');
    }
  };

  const dialog = (
    <TieBreakDialog
      open={Boolean(pending)}
      team={pending?.team}
      direction={pending?.direction}
      onCancel={cancel}
      onConfirm={confirm}
    />
  );

  return { requestMove: request, dialog };
}

function PodiumList({ teams, label, icon, expanded, scope, requestMove, showScores = true }) {
  const visible = expanded ? teams : teams.slice(0, 3);

  const handleMove = (team, direction) => {
    if (!requestMove || !scope) return;
    requestMove(scope, team, direction);
  };

  return (
    <div>
      {label && (
        <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <span className="text-2xl">{icon}</span> {label} {expanded ? 'Kedudukan Penuh' : '3 Teratas'}
        </h4>
      )}
      <div className="space-y-2">
        {visible.length === 0 && (
          <div className="text-sm text-zinc-500 italic">Belum ada markah.</div>
        )}
        {visible.map((team, idx) => {
          const tiedWithPrev =
            idx > 0 && visible[idx - 1].total_score.toFixed(6) === team.total_score.toFixed(6);
          const tiedWithNext =
            idx < visible.length - 1 &&
            visible[idx + 1].total_score.toFixed(6) === team.total_score.toFixed(6);
          const canMoveUp = tiedWithPrev && Boolean(scope);
          const canMoveDown = tiedWithNext && Boolean(scope);
          return (
            <div
              key={team.team_id}
              className="bg-zinc-900 border border-zinc-800 p-3 rounded-sm flex items-center gap-3"
            >
              <div
                className={`h-10 w-10 flex items-center justify-center rounded-full font-bold ${
                  idx === 0
                    ? 'bg-[#DFFF00] text-black'
                    : idx === 1
                    ? 'bg-zinc-700 text-white'
                    : 'bg-zinc-800 text-white'
                }`}
              >
                {idx + 1}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-white flex items-center gap-2">
                  {team.team_name}
                  {team.team_gender && !label && (
                    <span className="text-xs text-zinc-500">({team.team_gender === 'Boys' ? 'Lelaki' : 'Perempuan'})</span>
                  )}
                  {(tiedWithPrev || tiedWithNext) && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20">
                      Seri
                    </span>
                  )}
                </div>
                <div className="text-sm font-mono text-[#DFFF00]">
                  {showScores ? `${team.total_score.toFixed(2)} mata` : '••••'}
                </div>
              </div>
              {scope && (tiedWithPrev || tiedWithNext) && (
                <div className="flex flex-col items-end gap-1">
                  <span className="text-[10px] uppercase tracking-wider text-amber-300/80">
                    Pemecah Seri
                  </span>
                  <div className="flex items-center gap-1 bg-amber-500/5 border border-amber-500/30 rounded-sm px-1 py-0.5">
                    <Button
                      onClick={() => handleMove(team, 'up')}
                      disabled={!canMoveUp}
                      variant="ghost"
                      size="sm"
                      data-testid={`move-up-${team.team_id}`}
                      className="h-8 w-8 p-0 text-amber-300 hover:text-white hover:bg-amber-500/20 disabled:opacity-20"
                      title="Alih naik antara pasukan yang seri"
                    >
                      <ArrowUp className="h-5 w-5" />
                    </Button>
                    <Button
                      onClick={() => handleMove(team, 'down')}
                      disabled={!canMoveDown}
                      variant="ghost"
                      size="sm"
                      data-testid={`move-down-${team.team_id}`}
                      className="h-8 w-8 p-0 text-amber-300 hover:text-white hover:bg-amber-500/20 disabled:opacity-20"
                      title="Alih turun antara pasukan yang seri"
                    >
                      <ArrowDown className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CategoryPodium({ category, requestMove, showScores = true }) {
  const [expanded, setExpanded] = useState(false);
  const isConsolidated = category.category_name === 'Lagu Juara Terbaik Pilihan Juri';
  const scope = `category:${category.category_id}`;

  // For the consolidated category, teams are already ranked; re-sort by total
  // so boys and girls interleave correctly.
  const combinedTeams = [...category.rankings].sort(
    (a, b) => b.total_score - a.total_score || a.team_name.localeCompare(b.team_name)
  );
  const boysTeams = category.rankings.filter((t) => t.team_gender === 'Boys');
  const girlsTeams = category.rankings.filter((t) => t.team_gender === 'Girls');
  const hasMore = isConsolidated
    ? combinedTeams.length > 3
    : boysTeams.length > 3 || girlsTeams.length > 3;

  return (
    <div
      className="bg-[#18181b] border border-zinc-800 p-6 rounded-sm"
      data-testid={`category-${category.category_name}`}
    >
      <div className="flex items-center gap-3 mb-6">
        <Award className="h-6 w-6 text-[#DFFF00]" />
        <h3 className="font-heading text-2xl font-bold text-white">
          {category.category_name}
        </h3>
        {isConsolidated && (
          <span className="text-xs uppercase tracking-wider text-zinc-500 border border-zinc-700 rounded-full px-2 py-0.5">
            Lelaki + Perempuan Bergabung
          </span>
        )}
      </div>

      {isConsolidated ? (
        <PodiumList
          teams={combinedTeams}
          label=""
          icon=""
          expanded={expanded}
          scope={scope}
          requestMove={requestMove}
          showScores={showScores}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PodiumList teams={boysTeams} label="Lelaki" icon="♂" expanded={expanded} scope={scope} requestMove={requestMove} showScores={showScores} />
          <PodiumList teams={girlsTeams} label="Perempuan" icon="♀" expanded={expanded} scope={scope} requestMove={requestMove} showScores={showScores} />
        </div>
      )}

      {hasMore && (
        <div className="mt-6 pt-4 border-t border-zinc-800 flex justify-center">
          <Button
            onClick={() => setExpanded((e) => !e)}
            variant="ghost"
            data-testid={`expand-category-${category.category_name}`}
            className="text-zinc-400 hover:text-white gap-2"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-4 w-4" />
                Tunjuk 3 Teratas Sahaja
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" />
                Tunjuk Kedudukan Penuh
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}


function RankingsTable({ results, title, scope, requestMove, showScores = true }) {
  const topThree = results.slice(0, 3);
  const rest = results.slice(3);

  const scoreCounts = {};
  results.forEach((t) => {
    const k = t.total_score.toFixed(6);
    scoreCounts[k] = (scoreCounts[k] || 0) + 1;
  });
  const canSwap = Boolean(scope) && Boolean(requestMove);

  const move = (team, direction) => {
    if (!canSwap) return;
    requestMove(scope, team, direction);
  };

  return (
    <div className="space-y-6">
      <h2 className="font-heading text-2xl font-bold text-white">{title}</h2>

      {/* Top 3 Podium */}
      {topThree.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {topThree.map((team, idx) => (
            <div
              key={team.team_id}
              data-testid={`podium-team-${idx + 1}`}
              className={`bg-[#18181b] border p-6 rounded-sm ${
                idx === 0
                  ? 'border-[#DFFF00] glow-top md:col-start-2 md:row-start-1'
                  : idx === 1
                  ? 'border-zinc-700 md:col-start-1 md:row-start-2'
                  : 'border-zinc-700 md:col-start-3 md:row-start-2'
              }`}
            >
              <div className="text-center space-y-3">
                <div
                  className={`inline-flex h-16 w-16 items-center justify-center rounded-full ${
                    idx === 0
                      ? 'bg-[#DFFF00] text-black'
                      : idx === 1
                      ? 'bg-zinc-700 text-white'
                      : 'bg-zinc-800 text-white'
                  }`}
                >
                  <Trophy className="h-8 w-8" />
                </div>
                <div>
                  <div className="text-3xl font-mono font-bold text-white">#{team.rank}</div>
                  <div className="font-heading text-xl font-bold text-white mt-2">
                    {team.team_name}
                  </div>
                  <div className="text-sm text-zinc-500">{team.team_gender === 'Boys' ? 'Lelaki' : 'Perempuan'}</div>
                </div>
                <div className="pt-3 border-t border-zinc-800">
                  <div className="text-2xl font-mono font-bold text-[#DFFF00]">
                    {showScores ? team.total_score.toFixed(2) : '••••'}
                  </div>
                  <div className="text-xs text-zinc-500">Jumlah Markah</div>
                </div>
                {canSwap && scoreCounts[team.total_score.toFixed(6)] > 1 && (
                  <div className="pt-2 flex justify-center gap-2">
                    <Button
                      onClick={() => move(team, 'up')}
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-zinc-400 hover:text-white"
                      data-testid={`podium-move-up-${team.team_id}`}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => move(team, 'down')}
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-zinc-400 hover:text-white"
                      data-testid={`podium-move-down-${team.team_id}`}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rest of Rankings */}
      {rest.length > 0 && (
        <div className="bg-[#18181b] border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-900 border-b border-zinc-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                  Kedudukan
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                  Pasukan
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                  Jantina
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                  Markah
                </th>
                {canSwap && (
                  <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                    Pemecah Seri
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {rest.map((team, i) => {
                const restIdx = i + 3; // absolute index in results
                const key = team.total_score.toFixed(6);
                const tiedWithPrev =
                  restIdx > 0 && results[restIdx - 1].total_score.toFixed(6) === key;
                const tiedWithNext =
                  restIdx < results.length - 1 && results[restIdx + 1].total_score.toFixed(6) === key;
                const isTied = tiedWithPrev || tiedWithNext;
                return (
                  <tr key={team.team_id} className="hover:bg-zinc-900/50 transition-colors">
                    <td className="px-4 py-3 text-sm font-mono text-zinc-400">#{team.rank}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-white flex items-center gap-2">
                        {team.team_name}
                        {isTied && (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20">
                            Seri
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-400">{team.team_gender === 'Boys' ? 'Lelaki' : 'Perempuan'}</td>
                    <td className="px-4 py-3 text-right text-lg font-mono font-bold text-white">
                      {showScores ? team.total_score.toFixed(2) : '••••'}
                    </td>
                    {canSwap && (
                      <td className="px-4 py-3 text-right">
                        {isTied && (
                          <div className="inline-flex gap-1">
                            <Button
                              onClick={() => move(team, 'up')}
                              disabled={!tiedWithPrev}
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-zinc-400 hover:text-white disabled:opacity-20"
                              data-testid={`row-move-up-${team.team_id}`}
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => move(team, 'down')}
                              disabled={!tiedWithNext}
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-zinc-400 hover:text-white disabled:opacity-20"
                              data-testid={`row-move-down-${team.team_id}`}
                            >
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function JudgeScoresView({ allScores, subcategoryMap, showScores = true }) {
  const [selectedJudge, setSelectedJudge] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');

  // Get unique judges and teams
  const judges = [...new Set(allScores.map(s => s.judge_name))].sort();
  const teams = [...new Set(allScores.map(s => s.team_name))].sort();

  // Filter scores based on selection
  const filteredScores = allScores.filter(score => {
    if (selectedJudge && score.judge_name !== selectedJudge) return false;
    if (selectedTeam && score.team_name !== selectedTeam) return false;
    return true;
  });

  // Group by team for overview
  const scoresByTeam = {};
  filteredScores.forEach(score => {
    if (!scoresByTeam[score.team_name]) {
      scoresByTeam[score.team_name] = {
        team_name: score.team_name,
        team_gender: score.team_gender,
        scores: []
      };
    }
    scoresByTeam[score.team_name].scores.push(score);
  });

  const teamData = Object.values(scoresByTeam);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Eye className="h-6 w-6 text-[#DFFF00]" />
        <h2 className="font-heading text-2xl font-bold text-white">Markah Pengadil Individu</h2>
      </div>

      {/* Filters */}
      <div className="bg-[#18181b] border border-zinc-800 p-6 rounded-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Tapis Mengikut Pengadil</label>
            <select
              value={selectedJudge}
              onChange={(e) => setSelectedJudge(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-4 py-2 focus:border-[#DFFF00] focus:outline-none"
            >
              <option value="">Semua Pengadil</option>
              {judges.map(judge => (
                <option key={judge} value={judge}>{judge}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Tapis Mengikut Pasukan</label>
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-sm px-4 py-2 focus:border-[#DFFF00] focus:outline-none"
            >
              <option value="">Semua Pasukan</option>
              {teams.map(team => (
                <option key={team} value={team}>{team}</option>
              ))}
            </select>
          </div>
        </div>

        {(selectedJudge || selectedTeam) && (
          <div className="mt-4 flex items-center gap-2">
            <span className="text-sm text-zinc-400">
              Menunjukkan {filteredScores.length} markah
            </span>
            <button
              onClick={() => {
                setSelectedJudge('');
                setSelectedTeam('');
              }}
              className="text-sm text-[#DFFF00] hover:underline"
            >
              Buang tapisan
            </button>
          </div>
        )}
      </div>

      {/* Detailed Score Cards */}
      {teamData.map((team) => (
        <div
          key={team.team_name}
          className="bg-[#18181b] border border-zinc-800 p-6 rounded-sm"
        >
          <h3 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
            {team.team_name}
            <span className="text-sm text-zinc-500 font-normal">({team.team_gender === 'Boys' ? 'Lelaki' : 'Perempuan'})</span>
          </h3>

          <div className="space-y-4">
            {team.scores.map((score) => (
              <div
                key={score.id}
                className="bg-zinc-900/50 border border-zinc-800 p-4 rounded-sm"
              >
                {/* Score Header */}
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-zinc-800">
                  <div>
                    <div className="text-lg font-semibold text-white">{score.judge_name}</div>
                    <div className="text-sm text-zinc-400">{score.category_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-mono font-bold text-[#DFFF00]">
                      {showScores ? score.category_total.toFixed(2) : '••••'}
                    </div>
                    <div className="text-xs text-zinc-500">Jumlah Markah</div>
                  </div>
                </div>

                {/* Subcategory Breakdown */}
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-zinc-300 uppercase tracking-wider mb-2">
                    Pecahan Subkategori:
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {Object.entries(score.subcategory_scores).map(([subcatId, subcatScore]) => {
                      const meta = subcategoryMap[subcatId];
                      const label = meta ? meta.name : 'Subkategori';
                      const maxStr = meta ? ` / ${meta.max_score}` : '';
                      return (
                        <div
                          key={subcatId}
                          className="flex justify-between items-center gap-3 bg-zinc-900 px-3 py-2 rounded-sm"
                        >
                          <span className="text-sm text-zinc-400 truncate" title={label}>{label}</span>
                          <span className="font-mono text-white whitespace-nowrap">
                            {showScores ? `${subcatScore.toFixed(2)}${maxStr}` : '••••'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Deduction */}
                {score.deduction > 0 && (
                  <div className="mt-3 pt-3 border-t border-zinc-800">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-zinc-400">Potongan:</span>
                      <span className="font-mono text-red-400">{showScores ? `-${score.deduction.toFixed(2)}` : '••••'}</span>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {score.notes && (
                  <div className="mt-3 pt-3 border-t border-zinc-800">
                    <div className="text-sm text-zinc-400 mb-1">Nota:</div>
                    <div className="text-sm text-white italic">{score.notes}</div>
                  </div>
                )}

                {/* Timestamp */}
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <div className="text-xs text-zinc-500">
                    Dihantar: {new Date(score.timestamp).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {filteredScores.length === 0 && (
        <div className="bg-[#18181b] border border-zinc-800 p-8 rounded-sm text-center">
          <div className="text-zinc-500">Tiada markah dijumpai untuk tapisan dipilih</div>
        </div>
      )}
    </div>
  );
}

function JudgeLoginLockCard({ locked, busy, onToggle }) {
  return (
    <div className="bg-[#18181b] border border-zinc-800 p-6 rounded-sm">
      <div className="space-y-3">
        <h3 className="font-heading text-xl font-bold text-white flex items-center gap-2">
          {locked ? <Lock className="h-5 w-5 text-red-400" /> : <Unlock className="h-5 w-5 text-green-400" />}
          Log Masuk Pengadil {locked ? 'Dikunci' : 'Dibuka'}
        </h3>
        <p className="text-sm text-zinc-400">
          {locked
            ? 'Pengadil tidak dapat log masuk pada masa ini. Admin tidak terjejas.'
            : 'Pengadil boleh log masuk seperti biasa.'}
        </p>
        <Button
          onClick={onToggle}
          disabled={busy}
          data-testid="toggle-judge-login-lock-button"
          className={`w-full font-semibold ${
            locked
              ? 'bg-green-500 hover:bg-green-500/90 text-black'
              : 'bg-red-500 hover:bg-red-500/90 text-white'
          }`}
        >
          {locked ? 'Buka Log Masuk' : 'Kunci Log Masuk'}
        </Button>
      </div>
    </div>
  );
}

function TeamLocksPanel() {
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/team-locks`, { headers: authHeader() });
      setRows(r.data || []);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (row) => {
    setBusyId(row.team_id);
    try {
      await axios.patch(
        `${API}/admin/team-locks/${row.team_id}`,
        { locked: !row.locked },
        { headers: authHeader() }
      );
      toast.success(
        !row.locked
          ? `${row.team_name} dikunci — pengadil tidak boleh mengubah markah pasukan ini`
          : `${row.team_name} dibuka — pengadil boleh mengubah markah semula`
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal mengemaskini kunci pasukan');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-[#18181b] border border-zinc-800 p-6 rounded-sm space-y-4">
      <div>
        <h3 className="font-heading text-xl font-bold text-white flex items-center gap-2">
          <Lock className="h-5 w-5 text-[#DFFF00]" />
          Kunci Pasukan
        </h3>
        <p className="text-sm text-zinc-400 mt-1">
          Kunci pasukan tertentu untuk menghalang pengadil daripada mengubah markah pasukan tersebut.
          Pasukan lain tidak terjejas.
        </p>
      </div>

      {rows.length === 0 && (
        <div className="text-sm text-zinc-500 italic">Belum ada pasukan.</div>
      )}

      <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800">
        {rows.map((row) => (
          <div
            key={row.team_id}
            data-testid={`team-lock-row-${row.team_name}`}
            className="flex items-center justify-between gap-4 px-4 py-3"
          >
            <div className="flex items-center gap-3 min-w-0">
              {row.locked ? (
                <Lock className="h-4 w-4 text-red-400 shrink-0" />
              ) : (
                <Unlock className="h-4 w-4 text-green-400 shrink-0" />
              )}
              <span className="font-semibold text-white truncate">{row.team_name}</span>
              <span className="text-xs text-zinc-500">
                ({row.team_gender === 'Boys' ? 'Lelaki' : 'Perempuan'})
              </span>
            </div>
            <Button
              onClick={() => toggle(row)}
              disabled={busyId === row.team_id}
              data-testid={`team-lock-toggle-${row.team_name}`}
              className={`h-9 px-4 font-semibold ${
                row.locked
                  ? 'bg-green-500 hover:bg-green-500/90 text-black'
                  : 'bg-red-500 hover:bg-red-500/90 text-white'
              }`}
            >
              {row.locked ? 'Buka' : 'Kunci'}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function BengkelPanel({ reloadSignal }) {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState(null);
  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/bengkel-participation`, { headers: authHeader() });
      setRows(r.data || []);
      const initialDrafts = {};
      (r.data || []).forEach((row) => { initialDrafts[row.team_id] = String(row.marks ?? 0); });
      setDrafts(initialDrafts);
    } catch (e) {
      // silent
    }
  };
  useEffect(() => { load(); }, [reloadSignal]);

  const save = async (row) => {
    const val = parseFloat(drafts[row.team_id]);
    if (isNaN(val) || val < 0 || val > 10) {
      toast.error('Markah mesti antara 0 dan 10');
      return;
    }
    setBusyId(row.team_id);
    try {
      await axios.patch(
        `${API}/admin/bengkel-participation/${row.team_id}`,
        { marks: val },
        { headers: authHeader() }
      );
      toast.success(`Markah bengkel ${row.team_name} disimpan (${val})`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan markah bengkel');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-[#18181b] border border-zinc-800 p-6 rounded-sm space-y-4">
      <div>
        <h3 className="font-heading text-xl font-bold text-white flex items-center gap-2">
          <Award className="h-5 w-5 text-amber-400" />
          Markah Penyertaan Bengkel (Seni Kata)
        </h3>
        <p className="text-sm text-zinc-400 mt-1">
          Markah bengkel (0–10) untuk setiap pasukan. Akan ditambah kepada Seni Kata selepas markah asas didarab dengan 0.9.
          Contoh: Seni Kata mentah 100 → 90 + 8 bengkel = 98.
        </p>
      </div>

      {rows.length === 0 && (
        <div className="text-sm text-zinc-500 italic">Belum ada pasukan.</div>
      )}

      <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800">
        {rows.map((row) => {
          const raw = drafts[row.team_id];
          const num = raw === '' || raw === undefined || raw === null ? 0 : parseFloat(raw);
          const clamp = (v) => Math.min(10, Math.max(0, Math.round(v * 4) / 4));
          const setVal = (v) => setDrafts({ ...drafts, [row.team_id]: String(clamp(v)) });
          const dirty = String(drafts[row.team_id] ?? '') !== String(row.marks ?? '');
          return (
            <div
              key={row.team_id}
              data-testid={`bengkel-row-${row.team_name}`}
              className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-center px-4 py-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xs font-mono text-zinc-500 shrink-0">
                  {row.team_gender === 'Boys' ? 'L' : 'P'}#{row.display_order ?? '?'}
                </span>
                <span className="font-semibold text-white truncate">{row.team_name}</span>
              </div>
              <div className="flex items-center justify-self-start md:justify-self-end">
                <div className="inline-flex items-center rounded-full border border-zinc-700 bg-zinc-900 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setVal(num - 0.25)}
                    disabled={num <= 0}
                    data-testid={`bengkel-dec-${row.team_name}`}
                    className="h-9 w-9 flex items-center justify-center text-zinc-300 hover:bg-zinc-800 hover:text-[#DFFF00] transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                    aria-label="Kurang"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={drafts[row.team_id] ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^\d{0,2}(\.\d{0,2})?$/.test(v)) setDrafts({ ...drafts, [row.team_id]: v });
                    }}
                    onBlur={(e) => e.target.value !== '' && setVal(parseFloat(e.target.value) || 0)}
                    data-testid={`bengkel-input-${row.team_name}`}
                    className="w-12 h-9 bg-transparent border-x border-zinc-700 text-center font-mono text-base text-[#DFFF00] focus:outline-none focus:bg-zinc-800/60"
                  />
                  <button
                    type="button"
                    onClick={() => setVal(num + 0.25)}
                    disabled={num >= 10}
                    data-testid={`bengkel-inc-${row.team_name}`}
                    className="h-9 w-9 flex items-center justify-center text-zinc-300 hover:bg-zinc-800 hover:text-[#DFFF00] transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                    aria-label="Tambah"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <span className="text-xs text-zinc-500 ml-2 tabular-nums">/ 10</span>
              </div>
              <Button
                onClick={() => save(row)}
                disabled={busyId === row.team_id || !dirty}
                data-testid={`bengkel-save-${row.team_name}`}
                className="h-9 px-4 bg-[#DFFF00] hover:bg-[#DFFF00]/90 text-black font-semibold disabled:opacity-40"
              >
                Simpan
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AdminControlsPanel({ onRefresh, allScores }) {
  const [locked, setLocked] = useState(false);
  const [judgeLoginLocked, setJudgeLoginLocked] = useState(false);
  const [deductions, setDeductions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [judges, setJudges] = useState([]);
  const [dedTeam, setDedTeam] = useState('');
  const [dedPoints, setDedPoints] = useState('');
  const [dedReason, setDedReason] = useState('');
  const [resetJudge, setResetJudge] = useState('');
  const [resetTeam, setResetTeam] = useState('');
  const [busy, setBusy] = useState(false);
  const [bengkelReload, setBengkelReload] = useState(0);
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [lockDialogType, setLockDialogType] = useState('scoring'); // 'scoring' | 'judge_login'
  const [lockAdminName, setLockAdminName] = useState('');

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const loadAll = async () => {
    try {
      const [s, d, t, u] = await Promise.all([
        axios.get(`${API}/admin/settings`, { headers: authHeader() }),
        axios.get(`${API}/admin/deductions`, { headers: authHeader() }),
        axios.get(`${API}/teams`, { headers: authHeader() }),
        axios.get(`${API}/users/judges`, { headers: authHeader() }).catch(() => ({ data: [] })),
      ]);
      setLocked(Boolean(s.data.scoring_locked));
      setJudgeLoginLocked(Boolean(s.data.judge_login_locked));
      setDeductions(d.data || []);
      setTeams(t.data || []);
      setJudges(u.data || []);
    } catch (e) {
      // Fallback: build judges from allScores if the judges endpoint doesn't exist
      const uniq = {};
      (allScores || []).forEach((s) => {
        if (s.judge_id && !uniq[s.judge_id]) uniq[s.judge_id] = { id: s.judge_id, name: s.judge_name };
      });
      setJudges(Object.values(uniq));
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openLockDialog = (type = 'scoring') => {
    setLockDialogType(type);
    setLockAdminName('');
    setLockDialogOpen(true);
  };

  const confirmLockToggle = async () => {
    if (!lockAdminName.trim()) {
      toast.error('Sila masukkan nama anda');
      return;
    }
    setBusy(true);
    try {
      const isScoring = lockDialogType === 'scoring';
      const currentlyLocked = isScoring ? locked : judgeLoginLocked;
      const payload = isScoring
        ? { scoring_locked: !currentlyLocked, admin_name: lockAdminName.trim() }
        : { judge_login_locked: !currentlyLocked, admin_name: lockAdminName.trim() };
      const res = await axios.patch(`${API}/admin/settings`, payload, { headers: authHeader() });
      setLocked(Boolean(res.data.scoring_locked));
      setJudgeLoginLocked(Boolean(res.data.judge_login_locked));
      if (isScoring) {
        toast.success(res.data.scoring_locked ? 'Pemarkahan dikunci' : 'Pemarkahan dibuka');
      } else {
        toast.success(res.data.judge_login_locked ? 'Log masuk pengadil dikunci' : 'Log masuk pengadil dibuka');
      }
      setLockDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal mengemaskini kunci');
    } finally {
      setBusy(false);
    }
  };

  const addDeduction = async () => {
    if (!dedTeam || !dedPoints) {
      toast.error('Pilih pasukan dan masukkan markah');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/deductions`,
        { team_id: dedTeam, points: parseFloat(dedPoints), reason: dedReason },
        { headers: authHeader() }
      );
      toast.success('Potongan ditambah');
      setDedTeam('');
      setDedPoints('');
      setDedReason('');
      await loadAll();
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menambah potongan');
    } finally {
      setBusy(false);
    }
  };

  const removeDeduction = async (id) => {
    if (!window.confirm('Buang potongan ini?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/deductions/${id}`, { headers: authHeader() });
      toast.success('Potongan dibuang');
      await loadAll();
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error('Gagal membuang');
    } finally {
      setBusy(false);
    }
  };

  const runReset = async () => {
    const parts = [];
    if (resetJudge) {
      const jname = judges.find((j) => j.id === resetJudge)?.name || 'pengadil dipilih';
      parts.push(`pengadil "${jname}"`);
    }
    if (resetTeam) {
      const tname = teams.find((t) => t.id === resetTeam)?.name || 'pasukan dipilih';
      parts.push(`pasukan "${tname}"`);
    }
    const scopeStr = parts.length ? parts.join(' DAN ') : 'SEMUA PENGADIL DAN SEMUA PASUKAN';
    const adminNote = resetJudge
      ? ''
      : '\n\nMarkah Penyertaan Bengkel & potongan admin juga akan dipadam.';
    const confirmText = `Anda akan PADAM markah secara kekal untuk ${scopeStr}.${adminNote}\n\nTaip RESET untuk mengesahkan.`;
    const input = window.prompt(confirmText, '');
    if (input !== 'RESET') {
      toast.message('Reset dibatalkan');
      return;
    }
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (resetJudge) params.append('judge_id', resetJudge);
      if (resetTeam) params.append('team_id', resetTeam);
      const res = await axios.delete(
        `${API}/admin/scores${params.toString() ? '?' + params.toString() : ''}`,
        { headers: authHeader() }
      );
      toast.success(
        `Padam ${res.data.deleted} rekod markah` +
          (res.data.admin_marks_cleared
            ? ` + ${res.data.admin_marks_cleared} markah admin`
            : '')
      );
      setResetJudge('');
      setResetTeam('');
      await loadAll();
      setBengkelReload((n) => n + 1);
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error('Reset gagal');
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/admin/export.csv`, {
        headers: authHeader(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
      a.download = `dikir-senja-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV dimuat turun');
    } catch (e) {
      toast.error('Gagal eksport');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Export CSV */}
      <div className="bg-[#18181b] border border-zinc-800 p-6 rounded-sm flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading text-xl font-bold text-white flex items-center gap-2">
            <Download className="h-5 w-5 text-[#DFFF00]" />
            Eksport Semua Keputusan (CSV)
          </h3>
          <p className="text-sm text-zinc-400 mt-1">
            CSV tunggal dengan 5 bahagian — kedudukan mengikut jantina, keseluruhan, setiap kategori, pecahan subkategori setiap pengadil, dan pengiraan penuh di sebalik jumlah setiap pasukan.
          </p>
        </div>
        <Button
          onClick={downloadCsv}
          disabled={busy}
          data-testid="export-csv-button"
          className="bg-[#DFFF00] hover:bg-[#DFFF00]/90 text-black font-semibold"
        >
          <Download className="h-4 w-4 mr-2" />
          Muat Turun CSV
        </Button>
      </div>

      {/* Two global locks, side-by-side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Pemarkahan (Scoring) lock */}
        <div className="bg-[#18181b] border border-zinc-800 p-6 rounded-sm">
          <div className="space-y-3">
            <h3 className="font-heading text-xl font-bold text-white flex items-center gap-2">
              {locked ? <Lock className="h-5 w-5 text-red-400" /> : <Unlock className="h-5 w-5 text-green-400" />}
              Pemarkahan {locked ? 'Dikunci' : 'Dibuka'}
            </h3>
            <p className="text-sm text-zinc-400">
              {locked
                ? 'Pengadil tidak boleh menghantar atau mengemaskini sebarang markah pada masa ini.'
                : 'Pengadil boleh menghantar dan mengemaskini markah dengan bebas.'}
            </p>
            <Button
              onClick={() => openLockDialog('scoring')}
              disabled={busy}
              data-testid="toggle-lock-button"
              className={`w-full font-semibold ${
                locked
                  ? 'bg-green-500 hover:bg-green-500/90 text-black'
                  : 'bg-red-500 hover:bg-red-500/90 text-white'
              }`}
            >
              {locked ? 'Buka Pemarkahan' : 'Kunci Pemarkahan'}
            </Button>
          </div>
        </div>

        {/* Judge Login lock */}
        <JudgeLoginLockCard
          locked={judgeLoginLocked}
          busy={busy}
          onToggle={() => openLockDialog('judge_login')}
        />
      </div>

      {/* Per-team locks */}
      <TeamLocksPanel />

      {/* Bengkel Participation (admin-editable, judge-readable) */}
      <BengkelPanel reloadSignal={bengkelReload} />

      {/* Admin Deductions */}
      <div className="bg-[#18181b] border border-zinc-800 p-6 rounded-sm space-y-4">
        <h3 className="font-heading text-xl font-bold text-white flex items-center gap-2">
          <MinusCircle className="h-5 w-5 text-amber-400" />
          Potongan Markah Admin
        </h3>
        <p className="text-sm text-zinc-400">
          Tolak markah daripada <span className="text-white font-semibold">jumlah keseluruhan</span> pasukan. Lagu Juara tidak terjejas.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_1fr_auto] gap-3">
          <select
            value={dedTeam}
            onChange={(e) => setDedTeam(e.target.value)}
            data-testid="deduction-team-select"
            className="bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2"
          >
            <option value="">Pilih pasukan…</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.gender === 'Boys' ? 'Lelaki' : 'Perempuan'})
              </option>
            ))}
          </select>
          <input
            type="number"
            step="0.5"
            min="0"
            value={dedPoints}
            placeholder="Markah"
            onChange={(e) => setDedPoints(e.target.value)}
            data-testid="deduction-points-input"
            className="bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2"
          />
          <input
            type="text"
            value={dedReason}
            placeholder="Sebab / komen (pilihan)"
            onChange={(e) => setDedReason(e.target.value)}
            data-testid="deduction-reason-input"
            className="bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2"
          />
          <Button
            onClick={addDeduction}
            disabled={busy}
            data-testid="add-deduction-button"
            className="bg-[#DFFF00] hover:bg-[#DFFF00]/90 text-black font-semibold"
          >
            Tambah
          </Button>
        </div>
        {deductions.length === 0 ? (
          <div className="text-sm text-zinc-500 italic">Tiada potongan setakat ini.</div>
        ) : (
          <div className="divide-y divide-zinc-800 border border-zinc-800 rounded-sm">
            {deductions.map((d) => (
              <div key={d.id} className="flex items-center justify-between px-3 py-2 gap-3">
                <div className="flex-1">
                  <div className="text-white font-semibold">
                    {d.team_name} <span className="text-red-400 font-mono">−{d.points.toFixed(2)}</span>
                  </div>
                  {d.reason && <div className="text-xs text-zinc-500">{d.reason}</div>}
                </div>
                <Button
                  onClick={() => removeDeduction(d.id)}
                  variant="ghost"
                  size="sm"
                  className="text-zinc-400 hover:text-red-400"
                  data-testid={`remove-deduction-${d.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Master Reset */}
      <div className="bg-[#18181b] border border-red-500/30 p-6 rounded-sm space-y-4">
        <h3 className="font-heading text-xl font-bold text-red-400 flex items-center gap-2">
          <Trash2 className="h-5 w-5" />
          Reset Induk
        </h3>
        <p className="text-sm text-zinc-400">
          Memadam markah yang dihantar secara kekal. Gabungkan tapisan pengadil dan pasukan untuk reset terperinci. Biarkan kedua-duanya kosong untuk memadam SEMUA markah.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select
            value={resetJudge}
            onChange={(e) => setResetJudge(e.target.value)}
            data-testid="reset-judge-select"
            className="bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2"
          >
            <option value="">Semua pengadil</option>
            {judges.map((j) => (
              <option key={j.id} value={j.id}>
                {j.name}
              </option>
            ))}
          </select>
          <select
            value={resetTeam}
            onChange={(e) => setResetTeam(e.target.value)}
            data-testid="reset-team-select"
            className="bg-zinc-900 border border-zinc-700 text-white rounded-sm px-3 py-2"
          >
            <option value="">Semua pasukan</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.gender === 'Boys' ? 'Lelaki' : 'Perempuan'})
              </option>
            ))}
          </select>
          <Button
            onClick={runReset}
            disabled={busy}
            data-testid="master-reset-button"
            className="bg-red-500 hover:bg-red-500/90 text-white font-semibold"
          >
            Reset Markah…
          </Button>
        </div>
      </div>

      <Dialog open={lockDialogOpen} onOpenChange={setLockDialogOpen}>
        <DialogContent className="bg-[#18181b] border border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {(() => {
                const isScoring = lockDialogType === 'scoring';
                const currently = isScoring ? locked : judgeLoginLocked;
                const what = isScoring ? 'Pemarkahan' : 'Log Masuk Pengadil';
                return `${currently ? 'Buka' : 'Kunci'} ${what}`;
              })()}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {(() => {
                const isScoring = lockDialogType === 'scoring';
                const currently = isScoring ? locked : judgeLoginLocked;
                return `Sila masukkan nama anda sebelum ${currently ? 'membuka' : 'mengunci'}. Ia direkodkan dalam log audit.`;
              })()}
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-sm text-zinc-300 font-semibold block mb-1">Nama anda</label>
            <input
              type="text"
              autoFocus
              value={lockAdminName}
              onChange={(e) => setLockAdminName(e.target.value)}
              data-testid="lock-admin-name-input"
              className="w-full bg-zinc-900 border border-zinc-700 focus:border-[#DFFF00] focus:outline-none text-white rounded-sm px-3 py-2"
              placeholder="cth. Nazim"
            />
          </div>
          <DialogFooter>
            <Button onClick={() => setLockDialogOpen(false)} variant="ghost" className="text-zinc-300 hover:text-white" disabled={busy}>
              Batal
            </Button>
            <Button
              onClick={confirmLockToggle}
              disabled={busy}
              data-testid="lock-confirm-button"
              className={
                (lockDialogType === 'scoring' ? locked : judgeLoginLocked)
                  ? 'bg-green-500 hover:bg-green-500/90 text-black font-semibold'
                  : 'bg-red-500 hover:bg-red-500/90 text-white font-semibold'
              }
            >
              {busy ? 'Menyimpan…' : (lockDialogType === 'scoring' ? locked : judgeLoginLocked) ? 'Sahkan Buka' : 'Sahkan Kunci'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

